import React from "react";
import { Switch, Route, Redirect } from "react-router-dom";

import Login from "./../pages/Login/";
import Schedule from "./../pages/Schedules/";
import Properties from "./../pages/Properties/";
import EditProperty from "./../pages/EditProperty/";
import CreateProperty from "../pages/CreateProperty";
import Users from "../pages/Users";
import CreateUsers from "../pages/CreateUsers";
import EditUser from "../pages/EditUser";
import Offers from "../pages/Offers";
import SeeOffer from "../pages/SeeOffer";
import ShowPropertiesPerConsultantReport from "../pages/ShowPropertiesPerConsultantReport";
import ShowPropertiesPerNeighborhoodReport from "../pages/ShowPropertiesPerNeighborhoodReport";
import MyAccount from "../pages/MyAccount";
import ShowVisitsPerConsultantReport from "../pages/ShowVisitsPerConsultantReport";
import ShowRentsReport from "../pages/ShowRentsReport";
import PhotographerSchedule from "../pages/PhotographerSchedule";
import CreateFAQ from "../pages/CreateFAQ";
import ListFAQ from "../pages/ListFAQ";
import EditFAQ from "../pages/EditFAQ";
import PhotographerUploadImage from "../pages/PhotographerUploadImage";
import ShowActivesPropertiesPerPeriod from "../pages/ShowActivesPropertiesPerPeriod";
import ForgotPassword from "../pages/ForgotPassword";
import ResetPassword from "../pages/ResetPassword";
import RentalBelt from "../pages/RentalBelt";
import UploadRentalBelt from "../pages/UploadRentalBelt";
import ClientsWithoutContract from "../pages/ClientsWithoutContract";
import CreateGuarantee from "../pages/CreateGuarantee";
import ShowGuarantees from "../pages/ShowGuarantees";
import UpdateBulkConsultants from "../pages/UpdateBulkConsultants";
import CreateCommercialRegion from "../pages/CreateCommercialRegion";
import ShowCommercialRegions from "../pages/ShowCommercialRegions";
import ModelContract from "../pages/ContextModelContract";
import KanbanBoard from "../pages/Kanban/KanbanBoard";
import ShowContractTemplates from "../pages/ShowContractTemplates";
import ContractForm from "../pages/ContractForm";
import SendContractToSigners from "../pages/SendContractToSigners";
import Documents from "../pages/Documents";
import MyProperties from "../pages/MyProperties";
import MyPropertyDetails from "../pages/MyPropertiesDetails";
import PropertyDetails from "../pages/PropertyDetails";
import TeamsAdmin from "../pages/Teams";
import RestrictiveAnalysis from "../pages/RestrictiveAnalysis";
import StyleGuide from "../pages/StyleGuide";
import Notifications from "../pages/Notifications";
import PermissionRoute from "../components/PermissionRoute";
import { PERM } from "../utils/permissions";

const DocumentsIndexRedirect = () => <Redirect to="/documents/1" />;
const NotificationsIndexRedirect = () => <Redirect to="/notifications/1" />;

const Routes: React.FC = () => {
  return (
    <Switch>
      <Route path="/" exact component={Login} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/password/reset/:token" component={ResetPassword} />

      <PermissionRoute
        path="/schedules"
        component={Schedule}
        permissions={[...PERM.scheduleAny]}
      />

      <PermissionRoute
        path="/teams"
        component={TeamsAdmin}
        permissions={[PERM.teamsList, PERM.teamsCreate, PERM.teamsUpdate, PERM.teamsDelete]}
      />

      <PermissionRoute path="/properties/:page" component={Properties} permission={PERM.propertyList} />

      <PermissionRoute
        path="/property/:id/details"
        exact
        component={PropertyDetails}
        permissions={[PERM.propertyShow, PERM.propertyUpdate, "adm.property.details"]}
      />

      <PermissionRoute
        path="/property/:id"
        component={EditProperty}
        permissions={[PERM.propertyShow, PERM.propertyUpdate, "adm.property.details"]}
      />

      <PermissionRoute path="/my-properties/:page" exact component={MyProperties} permission={PERM.propertyPartner} />

      <PermissionRoute path="/my-properties/view/:id" exact component={MyPropertyDetails} permission={PERM.propertyDetails} />

      <PermissionRoute path="/create-property" component={CreateProperty} permission={PERM.propertyCreate} />

      <PermissionRoute path="/consultants/update" component={UpdateBulkConsultants} permission={PERM.bulkConsultants} />

      <PermissionRoute
        path="/show-properties-per-consultant-report"
        component={ShowPropertiesPerConsultantReport}
        permission={PERM.reportConsultant}
      />

      <PermissionRoute
        path="/show-properties-per-neighborhood-report"
        component={ShowPropertiesPerNeighborhoodReport}
        permission={PERM.reportNeighborhood}
      />

      <PermissionRoute
        path="/show-visits-per-consultant-report"
        component={ShowVisitsPerConsultantReport}
        permissions={[
          PERM.reportVisits,
          PERM.reportVisitsByOwner,
          PERM.reportVisitsByProperty,
        ]}
      />

      <PermissionRoute
        path="/show-actives-properties-per-period"
        component={ShowActivesPropertiesPerPeriod}
        permission={PERM.reportActives}
      />

      <PermissionRoute path="/show-rents-report" component={ShowRentsReport} permission={PERM.reportRents} />

      <PermissionRoute path="/users" component={Users} permission={PERM.usersList} />

      <PermissionRoute path="/create-user" component={CreateUsers} permission={PERM.usersCreate} />

      <PermissionRoute path="/edit-user/:id" component={EditUser} permission={PERM.usersUpdate} />

      <PermissionRoute path="/offers/:page" component={Offers} permission={PERM.offerList} />

      <PermissionRoute path="/see-offer/:offer_id" component={SeeOffer} permission={PERM.offerShow} />

      <PermissionRoute path="/my-account" component={MyAccount} authOnly />

      <PermissionRoute path="/photographers-schedule" component={PhotographerSchedule} permission={PERM.photoSchedule} />

      <PermissionRoute path="/photographer-upload-photos" component={PhotographerUploadImage} permission={PERM.photoUpload} />

      <PermissionRoute path="/create-faq" component={CreateFAQ} permission={PERM.faqCreate} />

      <PermissionRoute path="/list-faqs/:page" component={ListFAQ} permission={PERM.faqList} />

      <PermissionRoute path="/edit-faq/:id" component={EditFAQ} permission={PERM.faqUpdate} />

      <PermissionRoute path="/contract" component={RentalBelt} permission={PERM.rentalBelt} />

      <PermissionRoute path="/contract-upload" component={UploadRentalBelt} permission={PERM.rentalUpload} />

      <PermissionRoute path="/contract-model/:type" component={ModelContract} permission={PERM.contractsModels} />

      <PermissionRoute
        path="/clients-without-contract"
        component={ClientsWithoutContract}
        permission={PERM.clientsWithoutContract}
      />

      <PermissionRoute path="/create-guarantee" component={CreateGuarantee} permission={PERM.guaranteesCreate} />

      <PermissionRoute path="/show-guarantees" component={ShowGuarantees} permission={PERM.guaranteesList} />

      <PermissionRoute
        path="/create-commercial-region"
        component={CreateCommercialRegion}
        permission={PERM.commercialCreate}
      />

      <PermissionRoute
        path="/show-commercial-regions"
        component={ShowCommercialRegions}
        permission={PERM.commercialList}
      />

      <PermissionRoute path="/kanban/:boardId" component={KanbanBoard} exact permission={PERM.boardShow} />

      <PermissionRoute path="/show-contract-templates/:id" component={ContractForm} permission={PERM.contractsShow} />

      <PermissionRoute path="/show-contract-templates" component={ShowContractTemplates} permission={PERM.contractsModels} />

      <PermissionRoute path="/send-contract-to-signers" component={SendContractToSigners} permission={PERM.contractsSend} />

      <PermissionRoute path="/documents" exact component={DocumentsIndexRedirect} permission={PERM.documents} />

      <PermissionRoute path="/documents/:page" component={Documents} permission={PERM.documents} />

      <PermissionRoute path="/analise-restritiva" component={RestrictiveAnalysis} authOnly />

      <PermissionRoute path="/style-guide" component={StyleGuide} permission={PERM.styleGuide} />

      <PermissionRoute
        path="/notifications"
        exact
        component={NotificationsIndexRedirect}
        permissions={[PERM.notificationsLogs, PERM.notificationsAutomations]}
      />

      <PermissionRoute
        path="/notifications/:page"
        component={Notifications}
        permissions={[PERM.notificationsLogs, PERM.notificationsAutomations]}
      />
    </Switch>
  );
};

export default Routes;